import React, { useCallback, useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import StatusWarningIcon from '@atlaskit/icon/core/status-warning';
import { token } from '@atlaskit/tokens';

import { ConfluenceEdition } from '@confluence/change-edition/entry-points/ConfluenceEdition.ts';
import { fg } from '@confluence/feature-gating';
import { useRightSidebarContext } from '@confluence/page-layout-context';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import { useApprovals } from '../ApprovalsContext.tsx';
import { openApprovalsPaneForContext } from '../approvalsPaneSidebar.ts';
import { getNoonUtcDateFromDateOnly } from '../dueDateUtils.ts';

import type { BannerAvatarProps } from './BannerComponent.tsx';
import { BannerComponent } from './BannerComponent.tsx';

const i18n = defineMessages({
	approvalInProgressHeading: {
		id: 'approvals.banner.approval-in-progress.heading',
		defaultMessage: 'Approval in progress',
		description: 'Heading shown in the approval banner when a review is in progress',
	},
	approvedDescription: {
		id: 'approvals.banner.approved.description',
		defaultMessage: '{current}/{total} approved',
		description:
			'Description showing how many approvers have approved out of the total when there are no denials, e.g. "2/5 approved"',
	},
	respondedDescription: {
		id: 'approvals.banner.responded.description',
		defaultMessage: '{current}/{total} responded',
		description:
			'Description showing how many approvers have responded out of the total when there are denials, e.g. "3/5 responded"',
	},
	yourApprovalNeededHeading: {
		id: 'approvals.banner.your-approval-needed.heading',
		defaultMessage: 'Your approval is needed',
		description: 'Heading shown when the current user needs to provide their approval',
	},
	yourApprovalNeededByDueDateHeading: {
		id: 'approvals.banner.your-approval-needed-by-due-date.heading.ai-non-final',
		defaultMessage: 'Your approval is needed by {dueDate}',
		description:
			'Heading shown in the approval banner when the current user needs to provide approval by a due date. The {dueDate} placeholder is the formatted approval due date.',
	},
	approverRequestDescription: {
		id: 'approvals.banner.approver-request.description',
		defaultMessage: 'From {requestorName}: "{reviewComment}"',
		description:
			'Description showing who requested the approval and their message, e.g. From John: "Please review this page"',
	},
	respondCta: {
		id: 'approvals.banner.respond.cta',
		defaultMessage: 'Respond',
		description: 'Button label for an approver who has not yet responded to the approval request',
	},
	editResponseCta: {
		id: 'approvals.banner.edit-response.cta',
		defaultMessage: 'Edit response',
		description:
			'Button label for an approver who has already responded and wants to change their response',
	},
	viewDetailsCta: {
		id: 'approvals.banner.view-details.cta',
		defaultMessage: 'View details',
		description:
			'Button label for viewers and requestors to view the approval details in the side panel',
	},
	blockedApprovalHeading: {
		id: 'approvals.banner.blocked-approval.heading',
		defaultMessage: 'This approval needs attention.',
		description:
			'Heading shown to requesters and approvers when an approval is blocked because required approvers changed.',
	},
	seeDetailsCta: {
		id: 'approvals.banner.see-details.cta',
		defaultMessage: 'See details',
		description:
			'Button label shown to the requester to open details for a blocked approval requiring attention.',
	},
});

/**
 * ApprovalBanner - Smart wrapper around BannerComponent that consumes ApprovalsContext
 *
 * Renders the appropriate approval banner based on the current user's role:
 * - Approver who hasn't responded: "Your approval is needed" with "Respond" CTA
 * - Approver who has responded: "Approval in progress" with "Edit response" CTA
 * - Requestor / Viewer: "Approval in progress" with "View details" CTA
 *
 * Renders nothing if:
 * - Data is still loading
 * - There is no active review
 * - The review is not in PENDING status
 */
export const ApprovalBanner: React.FC = () => {
	const { formatDate, formatMessage } = useIntl();
	const { edition } = useSessionData();
	const {
		approvalRequest,
		approvalRequestLoading,
		currentUserApprover,
		contentId,
		contentStatus,
		isContentApprovalsEnabled,
		isCurrentUserRequestor,
		isPendingReviewBlockedByRequiredApproversChange,
	} = useApprovals();
	const rightSidebarContext = useRightSidebarContext();

	const openApprovalsPane = useCallback(() => {
		openApprovalsPaneForContext({
			contentId,
			contentStatus,
			rightSidebarContext,
		});
	}, [contentId, contentStatus, rightSidebarContext]);

	const approvers = approvalRequest?.review?.approvers;

	const avatarData = useMemo((): BannerAvatarProps[] => {
		if (!approvers?.length) return [];

		// Sort approvers by status, so that approvers that denied are shown first
		const statusOrder: Record<string, number> = {
			REVIEWER_DENIED: 0,
			REVIEWER_APPROVED: 1,
		};
		const defaultOrder = 2; // PENDING_REVIEW and any other statuses come last

		const sorted = [...approvers].sort(
			(a, b) => (statusOrder[a.status] ?? defaultOrder) - (statusOrder[b.status] ?? defaultOrder),
		);

		// Convert approvers to AvatarProps for a presentational AvatarGroup
		return sorted.map((approver): BannerAvatarProps => {
			let status: BannerAvatarProps['status'];
			if (approver.status === 'REVIEWER_APPROVED') {
				status = 'approved';
			} else if (approver.status === 'REVIEWER_DENIED') {
				status = 'declined';
			}

			const userId = approver.principalType === 'USER' ? approver.principalId : undefined;

			return {
				appearance: 'circle',
				src: approver.avatarUrl || '',
				name: approver.name || '',
				size: 'small',
				status,
				userId,
			};
		});
	}, [approvers]);

	const { responseCount, hasDenials } = useMemo(() => {
		let approvedCount = 0;
		let deniedCount = 0;
		for (const a of approvers ?? []) {
			if (a.status === 'REVIEWER_APPROVED') {
				approvedCount++;
			} else if (a.status === 'REVIEWER_DENIED') {
				deniedCount++;
			}
		}
		return {
			responseCount: approvedCount + deniedCount,
			hasDenials: deniedCount > 0,
		};
	}, [approvers]);

	const formattedDueDate = useMemo(() => {
		const dueDate = approvalRequest?.review?.dueDate;
		if (!dueDate) {
			return null;
		}

		const date = getNoonUtcDateFromDateOnly(dueDate);
		if (!date) {
			return null;
		}

		return formatDate(date, {
			year: 'numeric',
			month: 'short',
			day: 'numeric',
			timeZone: 'UTC',
		});
	}, [approvalRequest?.review?.dueDate, formatDate]);

	// Don't render for non-premium editions, when space-level approvals are disabled,
	// or if loading, no review, or review is not pending
	if (edition !== ConfluenceEdition.PREMIUM) return null;
	if (!isContentApprovalsEnabled) return null;
	if (approvalRequestLoading) return null;
	if (!approvalRequest?.review) return null;
	if (approvalRequest.review.reviewStatus !== 'PENDING') return null;

	const isApproverPendingResponse = currentUserApprover?.status === 'PENDING_REVIEW';
	const isApproverResponded =
		currentUserApprover != null && currentUserApprover.status !== 'PENDING_REVIEW';

	// Description text: "X/Y approved" when no denials, "X/Y responded" when there are denials
	const progressDescription = formatMessage(
		hasDenials ? i18n.respondedDescription : i18n.approvedDescription,
		{
			current: responseCount,
			total: approvers?.length ?? 0,
		},
	);

	if (isPendingReviewBlockedByRequiredApproversChange) {
		if (!isCurrentUserRequestor && !currentUserApprover) return null;

		return (
			<BannerComponent
				heading={formatMessage(i18n.blockedApprovalHeading)}
				icon={
					<StatusWarningIcon
						label={formatMessage(i18n.blockedApprovalHeading)}
						color={token('color.icon.warning')}
					/>
				}
				action={{
					type: 'button',
					label: formatMessage(i18n.seeDetailsCta),
					onClick: openApprovalsPane,
				}}
			/>
		);
	}

	// Approver who hasn't responded
	if (isApproverPendingResponse) {
		const reviewComment = approvalRequest.review.reviewComment || '';
		const requestorName = approvalRequest.review.requestorName || '';
		const hasReviewComment = Boolean(reviewComment);
		const heading = formattedDueDate
			? formatMessage(i18n.yourApprovalNeededByDueDateHeading, {
					dueDate: formattedDueDate,
				})
			: formatMessage(i18n.yourApprovalNeededHeading);

		return (
			<BannerComponent
				heading={heading}
				description={
					hasReviewComment
						? formatMessage(i18n.approverRequestDescription, { requestorName, reviewComment })
						: progressDescription
				}
				avatarData={hasReviewComment ? undefined : avatarData}
				showProfileCards={fg('confluence_approvals_open_beta_tweaks')}
				action={{
					type: 'button',
					label: formatMessage(i18n.respondCta),
					onClick: openApprovalsPane,
				}}
				showProgressBar={!hasReviewComment}
			/>
		);
	}

	// Approver who has responded, or Requestor/Viewer
	return (
		<BannerComponent
			heading={formatMessage(i18n.approvalInProgressHeading)}
			description={progressDescription}
			avatarData={avatarData}
			showProfileCards={fg('confluence_approvals_open_beta_tweaks')}
			action={{
				type: 'button',
				label: formatMessage(isApproverResponded ? i18n.editResponseCta : i18n.viewDetailsCta),
				onClick: openApprovalsPane,
			}}
			showProgressBar
		/>
	);
};
